import type { FC, ImgHTMLAttributes } from 'react';

import { css } from '@compiled/react';

import { Adg4RaisedMedia } from '../../raised-media/src/raised-media';

type ImageProps = {
	src: string;
	alt: string;
	className?: string;
};

export interface Adg4FramedImageProps extends ImgHTMLAttributes<HTMLImageElement> {
	feature: ImageProps;
	accent?: ImageProps;
	bgShape: string;
	bgColor: string;
	activeIndexIsEven?: boolean;
	lazyLoadImage?: boolean;
}

export const Adg4FramedImage: FC<Adg4FramedImageProps> = ({
	feature,
	accent,
	bgShape,
	bgColor,
	activeIndexIsEven,
	lazyLoadImage = false,
}: Adg4FramedImageProps) => {
	return (
		<div css={framedImageStyles}>
			<div css={imgRegion}>
				<div
					css={[
						bgShapeStyle,
						activeIndexIsEven === true && activeEvenBgStyle,
						activeIndexIsEven === false && activeOddBgStyle,
					]}
					style={{
						clipPath: `polygon(${bgShape})`,
						backgroundColor: bgColor,
					}}
				/>
				{accent ? (
					// eslint-disable-next-line no-restricted-syntax
					<img
						src={accent.src}
						className={accent.className}
						alt={accent.alt}
						loading={lazyLoadImage ? 'lazy' : undefined}
						css={[
							accentImageStyle,
							activeIndexIsEven === true && activeEvenImgStyle,
							activeIndexIsEven === false && activeOddImgStyle,
						]}
					/>
				) : (
					''
				)}
				<div
					css={[
						featureImageStyle,
						activeIndexIsEven === true && activeEvenImgStyle,
						activeIndexIsEven === false && activeOddImgStyle,
					]}
				>
					<Adg4RaisedMedia>
						{/* eslint-disable-next-line no-restricted-syntax */}
						<img
							className={feature.className}
							loading={lazyLoadImage ? 'lazy' : undefined}
							src={feature.src}
							alt={feature.alt}
						/>
					</Adg4RaisedMedia>
				</div>
			</div>
		</div>
	);
};

const framedImageStyles = css({
	'@keyframes imagefadeineven': {
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'0%': {
			opacity: 0,
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'100%': {
			opacity: 1,
		},
	},
	'@keyframes imagefadeinodd': {
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'0%': {
			opacity: 0,
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'100%': {
			opacity: 1,
		},
	},
	'@keyframes bgshapepulseodd': {
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'0%': {
			transform: 'scale(1)',
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'50%': {
			transform: 'scale(0.9)',
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'100%': {
			transform: 'scale(1)',
		},
	},
	'@keyframes bgshapepulseeven': {
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'0%': {
			transform: 'scale(1)',
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'50%': {
			transform: 'scale(0.9)',
		},
		// Tech debt: Adding this disable to unblock commits for GWP-2646
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'100%': {
			transform: 'scale(1)',
		},
	},
	position: 'relative',
});

const imgRegion = css({
	position: 'relative',
	aspectRatio: '7/4',
});

const featureImageStyle = css({
	position: 'relative',
	display: 'block',
	height: 'auto',
	width: '100%',
	padding: '7% 5%',
	// Tech debt: Adding this disable to unblock commits for GWP-2646
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		maxWidth: '100%',
	},
});

const accentImageStyle = css({
	width: '20%',
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockEnd: '-10%',
});

const bgShapeStyle = css({
	width: '100%',
	height: '100%',
	transition: 'all 1s',
	position: 'absolute',
	insetInlineStart: 0,
	insetBlockStart: 0,
});

const activeEvenBgStyle = css({
	animation: 'bgshapepulseeven ease-in-out 1s 1',
});

const activeOddBgStyle = css({
	animation: 'bgshapepulseodd ease-in-out 1s 1',
});
const activeEvenImgStyle = css({
	animation: 'imagefadeineven 0.25s 1',
});

const activeOddImgStyle = css({
	animation: 'imagefadeinodd 0.25s 1',
});
